'use client'

import { PropsWithChildren, createContext, use, useContext } from 'react'
import { UAParser } from 'ua-parser-js'
import { useIsClient, useMediaQuery } from 'usehooks-ts'

import { useTotalVramKb } from '@/hooks/useTotalVramKb'
import { getDefaultDeviceVramTitle, getDevice } from '@/lib/is-mobile'
import { DeviceType, DeviceTypeWithAndroidIOS } from '@/types/configs'

export type DefaultDeviceVramTitle = Awaited<ReturnType<typeof getDefaultDeviceVramTitle>>

type Device = Awaited<ReturnType<typeof getDevice>>

type UAContextType = {
  isAndroidMobile: boolean
  isMobileApp: boolean
  defaultDeviceVramTitle: DefaultDeviceVramTitle
  device: Device
  deviceType: DeviceType
  deviceTypeWithAndroidAndIOS: DeviceTypeWithAndroidIOS
  totalVramKb?: number
  browserName?: string
  isMobileLandscape: boolean
  isMobile: boolean
}

type UAProviderProps = PropsWithChildren<{
  isAndroidMobilePromise: Promise<boolean>
  isMobileAppPromise: Promise<boolean>
  defaultDeviceVramTitlePromise: Promise<DefaultDeviceVramTitle>
  devicePromise: Promise<Device>
  deviceTypePromise: Promise<DeviceType>
  deviceTypeWithAndroidAndIOSPromise: Promise<DeviceTypeWithAndroidIOS>
}>

const UAContext = createContext<UAContextType | null>(null)

export function UAProvider({
  children,
  isAndroidMobilePromise,
  isMobileAppPromise,
  defaultDeviceVramTitlePromise,
  devicePromise,
  deviceTypePromise,
  deviceTypeWithAndroidAndIOSPromise,
}: UAProviderProps) {
  const isAndroidMobile = use(isAndroidMobilePromise)
  const isMobileApp = use(isMobileAppPromise)
  const defaultDeviceVramTitle = use(defaultDeviceVramTitlePromise)
  const device = use(devicePromise)
  const deviceType = use(deviceTypePromise)
  const deviceTypeWithAndroidAndIOS = use(deviceTypeWithAndroidAndIOSPromise)

  const totalVramKb = useTotalVramKb(deviceType)
  const isClient = useIsClient()
  const isMobileLandscape = useMediaQuery('(orientation: landscape) and (max-height: 480px)')
  const isMobile = useMediaQuery('(max-width: 767px) or (max-height: 480px)')
  const { browser } = UAParser(isClient ? navigator.userAgent : '')
  const browserName = browser.name?.toLowerCase()

  return (
    <UAContext.Provider
      value={{
        isAndroidMobile,
        isMobileApp,
        defaultDeviceVramTitle,
        device,
        deviceType,
        deviceTypeWithAndroidAndIOS,
        totalVramKb,
        browserName,
        isMobileLandscape,
        isMobile,
      }}
    >
      {children}
    </UAContext.Provider>
  )
}

export const useUAContext = () => {
  const value = useContext(UAContext)

  if (value == null) {
    throw new Error('useUAContext cannot be used outside of UAProvider')
  }

  return value
}
